import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "#/modules/util.hooks/use-server-fn";
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@beweegbaas/ui";
import { m } from "#/paraglide/messages";
import { captureUnexpectedError } from "#/modules/util.errors/capture";
import { LayoutAuth } from "#/layouts/layout.auth";
import { forgotPasswordFn } from "#/modules/util.auth/auth.functions";
import { forgotPasswordSchema } from "#/modules/generic.login/schema.forgot-password";
import { useAppForm } from "#/modules/util.forms/form";

export const Route = createFileRoute("/$locale/{-$schoolyear}/forgot-password")({
  component: ForgotPasswordPage,
});

function ForgotPasswordPage() {
  const forgotPassword = useServerFn(forgotPasswordFn);
  const [done, setDone] = useState(false);

  const form = useAppForm({
    defaultValues: { email: "" },
    validators: { onSubmit: forgotPasswordSchema },
    onSubmit: async ({ value }) => {
      try {
        await forgotPassword({ data: value });
        setDone(true);
      } catch (error) {
        captureUnexpectedError(error, "forgot-password");
        form.setErrorMap({ onSubmit: { form: m["common.something_went_wrong"](), fields: {} } });
      }
    },
  });

  return (
    <LayoutAuth showVisuals={false}>
      <Card className="w-full">
        <CardHeader>
          <CardTitle>{m["auth.forgot_password.title"]()}</CardTitle>
          <CardDescription>{m["auth.forgot_password.description"]()}</CardDescription>
        </CardHeader>
        <CardContent>
          {done ? (
            <p className="text-sm text-[var(--sea-ink-soft)]">
              {m["auth.forgot_password.success_message"]()}
            </p>
          ) : (
            <form.AppForm>
              <form.FormRoot className="flex flex-col gap-4">
                <form.AppField
                  name="email"
                  children={(field) => <field.TextField label={m["common.fields.email"]()} />}
                />
                <form.SubmitButton className="w-full">
                  {m["auth.forgot_password.submit_button"]()}
                </form.SubmitButton>
                <div className="flex justify-end text-sm">
                  <Link
                    from={Route.fullPath}
                    className="text-blue-600 hover:underline dark:text-blue-400"
                    to="/$locale/{-$schoolyear}/login"
                    params={(prev: Record<string, string>) => prev}
                  >
                    {m["common.auth.back_to_login"]()}
                  </Link>
                </div>
              </form.FormRoot>
            </form.AppForm>
          )}
        </CardContent>
      </Card>
    </LayoutAuth>
  );
}
